import {
  TableContainer,
  Table,
  TableBody,
  Typography,
  Box,
  TableCell,
  Paper,
} from '@mui/material';
import KitchenSubOrder from '../../../../app/data/models/orders/kitchenSubOrder';
import { Check, Note as NoteIcon } from '@mui/icons-material';
import OrderItem from '../../../../app/data/models/orders/orderItem';
import { OrderStatus } from '../../../../app/data/models/orders/orderStatus';
import StyledTableRow from '../../../../app/components/StyledTableRow';

interface Props {
  kitchenOrder: KitchenSubOrder;
  onOrderItemClick: (orderItem: OrderItem) => void;
}

const KitchenOrderItem = ({ kitchenOrder, onOrderItemClick }: Props) => {
  const isOrderActive = ![OrderStatus.Ready, OrderStatus.Cancelled].includes(
    kitchenOrder.orderStatus
  );

  const renderOrderItem = (orderItem: OrderItem) => {
    return (
      <StyledTableRow
        key={orderItem.id}
        onClick={() => isOrderActive && onOrderItemClick(orderItem)}
        sx={{
          '&:last-child td, &:last-child th': { border: 0 },
          cursor: isOrderActive ? 'pointer' : 'default',
          '&:hover': {
            backgroundColor: isOrderActive ? 'rgba(0, 0, 0, 0.04)' : 'inherit',
          },
          opacity: isOrderActive ? 1 : 0.7,
        }}
      >
        {(kitchenOrder.orderStatus === OrderStatus.Processing ||
          kitchenOrder.orderStatus === OrderStatus.Pending) && (
          <TableCell sx={{ padding: 0, width: 16 }}>
            {orderItem.isCompleted ? (
              <Check color="success" sx={{ fontSize: 16 }} />
            ) : (
              <Box sx={{ width: 16 }} />
            )}
          </TableCell>
        )}
        <TableCell
          align="left"
          sx={{
            padding: 0,
            pl: kitchenOrder.orderStatus === OrderStatus.Processing ? 0 : 1,
          }}
        >
          <Typography variant="body2">{orderItem.name}</Typography>
          {orderItem.customizations?.map((customization) => (
            <Box key={customization.id} sx={{ mt: 0.25 }}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}
              >
                {customization.options
                  .filter((option) => option.quantity > 0)
                  .map((option) => (
                    <span key={option.id}>
                      {option.quantity}x {option.name}
                      {option.price > 0 && ` (+$${option.price.toFixed(2)})`}
                    </span>
                  ))}
              </Typography>
              {customization.internalNotes && (
                <Typography
                  variant="caption"
                  color="error"
                  sx={{ display: 'block', fontStyle: 'italic', mt: 0.5 }}
                >
                  Note: {customization.internalNotes}
                </Typography>
              )}
            </Box>
          ))}
        </TableCell>
        <TableCell align="right" sx={{ width: 32 }}>
          <Typography variant="body1" fontWeight="bold">
            {orderItem.quantity}
          </Typography>
        </TableCell>
      </StyledTableRow>
    );
  };

  return (
    <Box>
      <TableContainer>
        <Table
          aria-label="Kitchen Order Items"
          size="small"
          sx={{
            '& .MuiTableCell-root': {
              padding: '4px 4px',
              '&:first-of-type': { paddingLeft: 1 },
              '&:last-child': { paddingRight: 1 },
            },
          }}
        >
          <TableBody
            sx={{
              '& > *': { borderBottom: '1px solid rgba(224, 224, 224, 1)' },
              '& > *:last-child': {
                '& .MuiTableCell-root': {
                  borderBottom: 'none',
                },
              },
            }}
          >
            {kitchenOrder.orderItems.map(renderOrderItem)}
          </TableBody>
        </Table>
      </TableContainer>

      {kitchenOrder.notes && (
        <Box
          sx={{
            mt: 1.5,
            position: 'relative',
            '&:before': {
              content: '""',
              position: 'absolute',
              left: 0,
              top: 0,
              bottom: 0,
              width: 4,
              bgcolor: 'warning.main',
              borderRadius: '4px 0 0 4px',
            },
          }}
        >
          <Paper
            elevation={0}
            sx={{
              pl: 2.5,
              py: 1.5,
              pr: 2,
              bgcolor: 'warning.50',
              borderRadius: '0 4px 4px 0',
              border: '1px solid',
              borderColor: 'warning.100',
              borderLeft: 'none',
              boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
              <NoteIcon
                color="warning"
                fontSize="small"
                sx={{
                  mt: '2px',
                  flexShrink: 0,
                  color: 'warning.dark',
                }}
              />
              <Typography
                variant="body2"
                sx={{
                  color: 'grey.800',
                  lineHeight: 1.5,
                  whiteSpace: 'pre-line',
                }}
              >
                {kitchenOrder.notes}
              </Typography>
            </Box>
          </Paper>
        </Box>
      )}
    </Box>
  );
};

export default KitchenOrderItem;
